import type { TreeViewBaseItem } from "@mui/x-tree-view-pro";
import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import { FieldValues } from "react-hook-form";
import {
  RateBuilderControls,
  RateMethods,
  TreeMethods,
  UseRateBuilderPropsReturn,
} from "@sinch/components/application/RatelistBuilder/types";

// Contexts
const ContextRateBuilderTree = createContext<TreeMethods<any, any> | null>(null);
const ContextRateBuilderData = createContext<RateMethods<any> | null>(null);
const ContextRateBuilderControl = createContext<RateBuilderControls["history"] | null>(null);

// Hooks
export const useRateTreeContext = <TreeItem extends TreeViewBaseItem, TData extends FieldValues = FieldValues>() => {
  const context = useContext(ContextRateBuilderTree);
  if (!context) {
    throw new Error("useRateTreeContext must be used within a RateBuilderTreeProvider");
  }
  return context as TreeMethods<TreeItem, TData>;
};

export const useRateDataContext = <TData extends FieldValues = FieldValues>() => {
  const context = useContext(ContextRateBuilderData);
  if (!context) {
    throw new Error("useRateDataContext must be used within a RateBuilderTreeProvider");
  }
  return context as RateMethods<TData>;
};

export const useRateBuilderControlContext = () => {
  const context = useContext(ContextRateBuilderControl);
  if (!context) {
    throw new Error("useRateBuilderControlContext must be used within a RateBuilderTreeProvider");
  }
  return context as RateBuilderControls["history"];
};

// Providers
const RateBuilderTreeProvider = <TreeItem extends TreeViewBaseItem, TData extends FieldValues = FieldValues>({
  children,
  rateTreeBuilder,
}: PropsWithChildren<{ rateTreeBuilder: TreeMethods<TreeItem, TData> }>) => (
  <ContextRateBuilderTree.Provider value={rateTreeBuilder}>{children}</ContextRateBuilderTree.Provider>
);

const RateBuilderDataProvider = <TData extends FieldValues = FieldValues>({
  children,
  rateDataBuilder,
}: PropsWithChildren<{ rateDataBuilder: RateMethods<TData> }>) => (
  <ContextRateBuilderData.Provider value={rateDataBuilder}>{children}</ContextRateBuilderData.Provider>
);

export const RateBuilderProvider = <TreeItem extends TreeViewBaseItem, TData extends FieldValues = FieldValues>({
  children,
  rateBuilder,
}: PropsWithChildren<{ rateBuilder: UseRateBuilderPropsReturn<TreeItem, TData> }>) => {
  const { tree, add, remove, copy, move, getParent, set, getIndex, get, expanded, expand } = rateBuilder;
  const { data, changedFields, setData, initialData } = rateBuilder;
  const { revert, forward, revertSteps, forwardSteps } = rateBuilder.history;

  const treeContext = useMemo(
    () => ({ tree, add, remove, copy, move, getParent, getIndex, get, set, expanded, expand }),
    [tree, expanded]
  );
  const dataContext = useMemo(() => ({ data, changedFields, setData, initialData }), [data]);
  const controlContext = useMemo(() => ({ revert, forward, revertSteps, forwardSteps }), [data, tree]);

  return (
    <RateBuilderTreeProvider rateTreeBuilder={treeContext}>
      <RateBuilderDataProvider rateDataBuilder={dataContext}>
        <ContextRateBuilderControl.Provider value={controlContext}>{children}</ContextRateBuilderControl.Provider>
      </RateBuilderDataProvider>
    </RateBuilderTreeProvider>
  );
};
